/**
 * Comprehensive Logout Service
 *
 * This service handles complete cleanup of all authentication-related data
 * across the entire application, following enterprise-grade security practices.
 */

import { getLoginUrl } from '../utils/subdomain';
import { useSubscriptionStore } from '../stores/subscriptionStore';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;
const LOGOUT_ENDPOINT = import.meta.env.VITE_LOGOUT_ENDPOINT;
/**
 * Clear all localStorage keys related to authentication
 */
const clearLocalStorage = () => {
  const keysToRemove = [
    // Shell App - Backend OAuth tokens
    'access_token',
    'refresh_token',
    'user',
    'account',

    // Shell App - Keycloak tokens
    'kc-token',
    'kc-refreshToken',
    'trainery_auth', // Deprecated but might still exist

    // Admin Portal tokens
    'admin_access_token',
    'admin_refresh_token',
    'admin_user',
    'admin_account',

    // Zustand persisted stores
    'review-cloud-auth', // Review Cloud auth store
    'workforce-auth', // Workforce auth store (if exists)
    'subscription-storage', // Shell app subscription store

    // User preferences (must be cleared on org switch)
    'workforce-view-storage', // Workforce view mode (list/board/chart)
    'workforce-roles-storage', // Workforce roles and account info
    'bulk-import-draft', // Bulk import draft data
    'checkin-storage', // Review cloud check-in filters
    'report-builder-store', // Report builder state
    'report-builder-saved-reports', // Saved reports (legacy)
    'job-builder-store', // Comp builder job state
    'job-architecture-store', // Comp builder architecture state
    'learn-courses-view-mode', // Learn courses view preference

    // Any other potential auth keys
    'authToken',
    'token',
    'jwt_token',
  ];

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
      console.log(`✅ Cleared localStorage key: ${key}`);
    } catch (error) {
      console.warn(`⚠️ Failed to remove localStorage key ${key}:`, error);
    }
  });

  // Clear any keys that match auth patterns
  try {
    const allKeys = Object.keys(localStorage);
    const authPatterns = [/token/i, /auth/i, /user/i, /account/i, /session/i];

    allKeys.forEach(key => {
      const matchesPattern = authPatterns.some(pattern => pattern.test(key));
      if (matchesPattern && !keysToRemove.includes(key)) {
        localStorage.removeItem(key);
        console.log(`✅ Cleared additional auth-related key: ${key}`);
      }
    });
  } catch (error) {
    console.warn('⚠️ Failed to scan localStorage for additional auth keys:', error);
  }
};

/**
 * Clear all sessionStorage keys related to authentication
 */
const clearSessionStorage = () => {
  const keysToRemove = [
    // OAuth CSRF protection
    'oauth_state',
    'oauth_flow',

    // Any other session-based auth data
    'authToken',
    'access_token',

    // Cached UI data (must be cleared on account switch)
    'account_logo',
  ];

  keysToRemove.forEach(key => {
    try {
      sessionStorage.removeItem(key);
      console.log(`✅ Cleared sessionStorage key: ${key}`);
    } catch (error) {
      console.warn(`⚠️ Failed to remove sessionStorage key ${key}:`, error);
    }
  });
};

/**
 * Clear all cookies (if any exist)
 */
const clearCookies = () => {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';');

    // Clear each cookie
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Clear cookie for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      // Clear cookie for parent domain (if applicable)
      const domain = window.location.hostname;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;

      // Clear cookie for root domain
      const rootDomain = domain.split('.').slice(-2).join('.');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;

      console.log(`✅ Cleared cookie: ${name}`);
    });
  } catch (error) {
    console.warn('⚠️ Failed to clear cookies:', error);
  }
};

/**
 * Clear any IndexedDB data (used by some auth libraries)
 */
const clearIndexedDB = async () => {
  try {
    if ('indexedDB' in window) {
      const databases = await indexedDB.databases?.();

      if (databases) {
        for (const db of databases) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
            console.log(`✅ Cleared IndexedDB: ${db.name}`);
          }
        }
      }
    }
  } catch (error) {
    console.warn('⚠️ Failed to clear IndexedDB:', error);
  }
};

/**
 * Notify backend about logout (best practice for session invalidation)
 */
const notifyBackendLogout = async () => {
  try {
    const token = localStorage.getItem('access_token') ||
      localStorage.getItem('kc-token') ||
      localStorage.getItem('admin_access_token');

    if (token) {
      // Call backend logout endpoint
      await fetch(LOGOUT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('✅ Backend logout notification sent');
    }
  } catch (error) {
    // Fail silently - logout should proceed even if backend call fails
    console.warn('⚠️ Backend logout notification failed:', error);
  }
};

/**
 * Revoke Keycloak session (if using Keycloak)
 */
const revokeKeycloakSession = () => {
  try {
    // Check if Keycloak instance exists in window
    const kc = (window as any).keycloak;

    if (kc && typeof kc.logout === 'function') {
      // This will redirect to Keycloak logout page
      kc.logout();
      console.log('✅ Keycloak logout initiated');
      return true;
    }
  } catch (error) {
    console.warn('⚠️ Keycloak logout failed:', error);
  }
  return false;
};

/**
 * Clear Zustand stores programmatically
 */
const clearZustandStores = () => {
  try {
    // Dispatch storage events to clear persisted Zustand stores
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'review-cloud-auth',
      newValue: null,
      oldValue: localStorage.getItem('review-cloud-auth'),
    }));

    // Clear subscription store in-memory state
    useSubscriptionStore.getState().clearSubscriptions();

    console.log('✅ Zustand stores cleared');
  } catch (error) {
    console.warn('⚠️ Failed to clear Zustand stores:', error);
  }
};

/**
 * Main logout function - Comprehensive cleanup
 *
 * This function performs a complete logout by:
 * 1. Notifying the backend to invalidate server-side sessions
 * 2. Clearing all client-side storage (localStorage, sessionStorage, cookies)
 * 3. Clearing IndexedDB (if used)
 * 4. Clearing Zustand persisted stores
 * 5. Revoking Keycloak session (if applicable)
 * 6. Redirecting to login page
 *
 * @param redirectPath - Optional custom redirect path (defaults to /login)
 */
export const performLogout = async (redirectPath: string = getLoginUrl()) => {
  console.log('🔓 Starting comprehensive logout process...');

  try {
    // Step 1: Notify backend to invalidate session
    await notifyBackendLogout();

    // Step 2: Clear all storage
    clearLocalStorage();
    clearSessionStorage();
    clearCookies();
    clearZustandStores();

    // Step 3: Clear IndexedDB (async, but don't wait)
    clearIndexedDB().catch(err => console.warn('IndexedDB clear failed:', err));

    // Step 4: Check if using Keycloak and revoke session
    const keycloakLoggedOut = revokeKeycloakSession();

    // Step 5: Redirect to login (if Keycloak didn't handle redirect)
    if (!keycloakLoggedOut) {
      console.log('✅ Logout complete - Redirecting to login page');

      // Small delay to ensure all cleanup completes
      setTimeout(() => {
        window.location.replace(redirectPath);
      }, 100);
    }

  } catch (error) {
    console.error('❌ Error during logout:', error);

    // Even if there's an error, try to redirect
    window.location.replace(redirectPath);
  }
};

/**
 * Admin-specific logout function
 * Clears admin-specific data and redirects to admin login
 */
export const performAdminLogout = async () => {
  console.log('🔓 Starting admin logout process...');
  await performLogout('/login?redirect=/tnadmin/client');
};

/**
 * Quick logout without backend notification
 * Useful for emergency logout or when backend is unavailable
 */
export const performQuickLogout = (redirectPath: string = getLoginUrl()) => {
  console.log('🔓 Performing quick logout...');

  clearLocalStorage();
  clearSessionStorage();
  clearCookies();
  clearZustandStores();

  window.location.replace(redirectPath);
};

export default {
  performLogout,
  performAdminLogout,
  performQuickLogout,
};

